import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconMessageSquare } from '../Icons';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { money } from '../../utils/propFirmPresets';
import Explain from './Explain';

/*
 * One Prop Firm Planner row opened up, in three tabs:
 *  - Summary: how the tries ended, in plain sentences, and a typical day.
 *  - Day by day: one try replayed, as a chart of the balance against the
 *    max-loss floor, with the day table underneath.
 *  - How it's worked out: the scoring steps, the assumptions and the spread.
 * Every figure opens a pop-up showing how it was worked out. A "try" is one
 * evaluation started on one date; days are Day 1, Day 2, … (no dates).
 */

const SMALL_SAMPLE = 250; // start dates; about a year of trading
const pct = (n, of) => (of ? `${Math.round((n / of) * 100)}%` : '—');
const pct1 = (n, of) => (of ? `${((n / of) * 100).toFixed(1)}%` : '—');
const plural = (n, one, many = `${one}s`) => `${Number(n || 0).toLocaleString()} ${n === 1 ? one : many}`;
const signed = (n) => (n == null ? '—' : `${n < 0 ? '−' : '+'}${money(Math.abs(n))}`);
const X = Explain;

/** Plain-language steps for how one try is scored. */
export function evaluationSteps(rules, size, windowDays) {
  if (!rules) return [];
  const start = rules.accountSize;
  const steps = [
    `Start at ${money(start)} trading ${size}. Each day adds the strategy’s result for that day at ${size}.`,
  ];
  const pass = [`profit reaches ${money(rules.profitTarget)}`];
  if (rules.consistency) pass.push(`your best day is no more than ${rules.consistency}% of total profit`);
  if (rules.minDays) pass.push(`you’ve traded on at least ${plural(rules.minDays, 'day')}`);
  steps.push(`Pass on the first day ${pass.join(', and ')}.`);
  if (rules.trailing === 'static') {
    steps.push(`Fail if a day closes at or below ${money(start - rules.drawdown)} (the max loss). That floor never moves.`);
  } else {
    const lock = start + (rules.lockOffset || 0);
    steps.push(
      `Fail if a day closes at or below the max-loss floor. It starts at ${money(start - rules.drawdown)}, `
      + `rises ${money(rules.drawdown)} below each new high close, and stops rising at ${money(lock)}.`
      + (rules.trailing === 'intraday' ? ' The firm checks this during the day; the replay only sees each day’s close.' : ''),
    );
  }
  if (rules.dailyLossLimit) {
    if (rules.dailyLoss === 'fail') {
      steps.push(`A day that loses ${money(rules.dailyLossLimit)} or more fails the evaluation.`);
    } else if (rules.dailyLossCount === 'capped') {
      steps.push(`A day that loses ${money(rules.dailyLossLimit)} or more stops trading for the day, counted as a ${money(rules.dailyLossLimit)} loss.`);
    } else {
      steps.push(`A day that loses ${money(rules.dailyLossLimit)} or more stops trading for the day. Its full loss counts (the cautious choice).`);
    }
  }
  steps.push(`Out of time if neither happens within ${plural(windowDays, 'trading day')}. Days the strategy didn’t trade still count.`);
  return steps;
}

export function warningText(warning, rules) {
  if (warning === 'losses-over-limit') {
    return `Too risky for this account: its typical losing day at this size is bigger than the ${money(rules?.dailyLossLimit)} daily loss limit. `
      + 'The firm would also stop you on days that dipped that far before ending as a win, which daily results can’t show, so its odds are likely better here than in real life. '
      + 'It’s never picked as a strategy’s best size.';
  }
  if (warning === 'losses-over-half-drawdown') {
    return `Too risky for this account: its typical losing day at this size is half the ${money(rules?.drawdown)} max loss or more, so two bad days in a row end the evaluation. `
      + 'It’s never picked as a strategy’s best size.';
  }
  return '';
}

function Bar({ parts }) {
  const total = parts.reduce((s, p) => s + p.value, 0) || 1;
  return (
    <div className="pfd-bar" role="img" aria-label={parts.map((p) => `${p.label} ${pct(p.value, total)}`).join(', ')}>
      {parts.filter((p) => p.value > 0).map((p) => (
        <i key={p.label} className={`pfd-bar-${p.tone}`} style={{ width: `${(p.value / total) * 100}%` }} />
      ))}
    </div>
  );
}

const Steps = ({ items }) => <ol className="pfx-steps">{items.filter(Boolean).map((t) => <li key={t}>{t}</li>)}</ol>;
const Formula = ({ children }) => <p className="pfx-formula">{children}</p>;

/** Plain-language working for each figure. */
function explainers({ row, rules, windowDays, d, c, runs, day }) {
  const tooRecent = d.tooRecent || 0;
  const scale = day.scale ?? null;
  const perNq = (v) => (scale ? signed(v / scale) : '—');
  const passRule = [
    `profit reached ${money(rules?.profitTarget)}`,
    rules?.consistency ? `the best day was no more than ${rules.consistency}% of profit` : null,
    rules?.minDays ? `it had traded on at least ${plural(rules.minDays, 'day')}` : null,
  ].filter(Boolean).join(', ');
  const startLine = `A fresh ${money(rules?.accountSize)} evaluation was started on each of the strategy’s last ${plural(d.startsTried || runs, 'trading day')}${tooRecent ? `. The ${plural(tooRecent, 'most recent start')} couldn’t finish before the data ends, so ${tooRecent === 1 ? 'it isn’t' : 'they aren’t'} counted` : ''}.`;
  return {
    runs: (
      <Steps items={[
        startLine,
        `That leaves ${plural(runs, 'try', 'tries')} that ended one way or another: passed, failed, or ran out of time.`,
        'Tries overlap (one starting Tuesday shares most days with Monday’s), so they aren’t independent. They show how the outcome depends on when you start.',
      ]}
      />
    ),
    passed: (
      <>
        <Steps items={[
          startLine,
          `A try passes on the first day ${passRule}, before any day closed at or below the max-loss floor, within ${plural(windowDays, 'trading day')}.`,
          `${plural(c.passed, 'try', 'tries')} did that.`,
        ]}
        />
        <Formula>{c.passed.toLocaleString()} passed ÷ {runs.toLocaleString()} tries = {pct1(c.passed, runs)}</Formula>
        {d.alternative ? (
          <p className="pfx-note">
            {d.alternative.dailyLossCount === 'capped'
              ? `If days past the daily loss limit were counted only at the limit (the firm stopping you there), it would be ${d.alternative.passRate}%.`
              : `Counting the full loss on days past the daily loss limit (instead of only the limit) it would be ${d.alternative.passRate}%.`}
          </p>
        ) : null}
      </>
    ),
    breached: (
      <>
        <Steps items={[
          `A try fails when a day closes at or below the max-loss floor${rules?.dailyLoss === 'fail' && rules?.dailyLossLimit ? `, or a day loses ${money(rules.dailyLossLimit)} or more (this plan fails you for that)` : ''}.`,
          d.breaches?.byDailyLoss ? `${plural(d.breaches.byDrawdown, 'try', 'tries')} hit the max loss and ${plural(d.breaches.byDailyLoss, 'try', 'tries')} the daily loss limit.` : `${plural(c.breached, 'try', 'tries')} hit the max loss.`,
          d.breaches?.medianDay ? `Half of them failed by day ${d.breaches.medianDay}.` : null,
        ]}
        />
        <Formula>{c.breached.toLocaleString()} failed ÷ {runs.toLocaleString()} tries = {pct1(c.breached, runs)}</Formula>
      </>
    ),
    timeout: (
      <>
        <Steps items={[
          `A try runs out of time when it neither passes nor fails within ${plural(windowDays, 'trading day')}. Weekdays the strategy didn’t trade still use up time.`,
          c.timeout ? `Where they ended: a median of ${signed(d.timeouts?.medianProfit)} profit.` : null,
          d.timeouts?.reachedTarget ? `${plural(d.timeouts.reachedTarget, 'of them', 'of them')} had reached the target but not the consistency rule or minimum days.` : null,
        ]}
        />
        <Formula>{c.timeout.toLocaleString()} out of time ÷ {runs.toLocaleString()} tries = {pct1(c.timeout, runs)}</Formula>
      </>
    ),
    daysToPass: d.daysToPass ? (
      <Steps items={[
        'Each passing try’s days are counted from its start, including weekdays with no trade.',
        `The ${plural(c.passed, 'passing try', 'passing tries')} sorted from fastest to slowest: the fastest took ${d.daysToPass.fastest}, the middle one (median) ${d.daysToPass.median}, the slowest ${d.daysToPass.slowest}.`,
        `“Usually” is the middle half: from the 25th to the 75th percentile, ${d.daysToPass.p25}–${d.daysToPass.p75} days.`,
        d.waited?.count ? `${plural(d.waited.count, 'pass', 'passes')} reached the target early but had to keep trading for the consistency rule or minimum days; those extra days are included.` : null,
      ]}
      />
    ) : <p>No try passed, so there’s nothing to count.</p>,
    cushion: (
      <Steps items={[
        'Each day, the room left is the balance minus the max-loss floor: how much more it could lose before failing.',
        'Each try’s closest call is its smallest room left on any day.',
        `Across the ${plural(runs, 'try', 'tries')}, the middle one (median) is ${money(d.cushion?.median)}. The tightest got within ${money(Math.max(0, d.cushion?.worst ?? 0))}${(d.cushion?.worst ?? 1) <= 0 ? ' (it failed)' : ''}.`,
      ]}
      />
    ),
    scale: (
      <Steps items={[
        'Results are recorded per 1 NQ contract. 1 MNQ (micro) is a tenth of 1 NQ.',
        scale != null ? `At ${row.size}, every day’s result is ${scale === 1 ? 'used as is' : `multiplied by ${scale}`}.` : null,
        'Results are as recorded on the strategy’s Results page (fees as included there).',
      ]}
      />
    ),
    winRate: (
      <>
        <Steps items={[
          `Of ${plural(day.days, 'day')} with a trade, ${plural(day.winDays, 'day')} ended up, ${plural(day.lossDays, 'day')} ended down${day.evenDays ? ` and ${plural(day.evenDays, 'day')} broke even` : ''}.`,
          day.flatDays ? `${plural(day.flatDays, 'weekday')} with no trade aren’t counted here (they do count as time in the replay).` : null,
        ]}
        />
        <Formula>{day.winDays?.toLocaleString()} winning days ÷ {day.days?.toLocaleString()} days = {day.winRate}%</Formula>
      </>
    ),
    avgDay: <Formula>Total {signed(day.total)} ÷ {day.days?.toLocaleString()} days with a trade = {signed(day.avgDay)}</Formula>,
    avgWin: <Formula>All winning days {signed(day.sumWins)} ÷ {day.winDays?.toLocaleString()} winning days = {signed(day.avgWin)}</Formula>,
    avgLoss: (
      <>
        <Formula>All losing days {signed(day.sumLosses)} ÷ {day.lossDays?.toLocaleString()} losing days = {signed(day.avgLoss)}</Formula>
        {row.warning ? <p className="pfx-note">{warningText(row.warning, rules)}</p> : null}
      </>
    ),
    bestDay: (
      <Formula>Best single day: {perNq(day.bestDay)} per NQ {scale != null && scale !== 1 ? `× ${scale}` : ''} = {signed(day.bestDay)} at {row.size}</Formula>
    ),
    worstDay: (
      <>
        <Formula>Worst single day: {perNq(day.worstDay)} per NQ {scale != null && scale !== 1 ? `× ${scale}` : ''} = {signed(day.worstDay)} at {row.size}</Formula>
        {rules?.dailyLossLimit ? <p>{plural(day.overLimitDays, 'day')} at this size lost {money(rules.dailyLossLimit)} (the daily loss limit) or more.</p> : null}
        {day.overDrawdownDays ? <p>{plural(day.overDrawdownDays, 'day')} lost {money(rules?.drawdown)} or more: the whole max loss in one day.</p> : null}
      </>
    ),
  };
}

/**
 * One try as a chart: the balance (line), the max-loss floor (red dashed) and
 * the target (green dashed), Day 0 (the start) to the day it ended.
 */
export function BalanceChart({ steps, rules, outcome }) {
  // Drawn at the width it's shown, so labels stay readable on a phone.
  const ref = useRef(null);
  const [width, setWidth] = useState(640);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => { if (el.clientWidth) setWidth(Math.max(280, Math.round(el.clientWidth))); };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!steps?.length || !rules) return null;
  const start = rules.accountSize;
  const target = start + rules.profitTarget;
  const pts = [{ day: 0, balance: start, floor: start - rules.drawdown }, ...steps];
  const values = pts.flatMap((p) => [p.balance, p.floor]).concat(target);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const pad = (hi - lo) * 0.08 || 100;
  const W = width; const H = width < 480 ? 200 : 240; const L = 64; const R = 14; const T = 12; const B = 26;
  const x = (day) => L + ((W - L - R) * day) / Math.max(1, pts[pts.length - 1].day);
  const y = (v) => T + (H - T - B) * (1 - (v - (lo - pad)) / ((hi + pad) - (lo - pad)));
  const line = (key) => pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.day).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ');
  // The floor only changes at a close, so draw it as steps.
  const floorPath = pts.map((p, i) => (i ? `H${x(p.day).toFixed(1)} V${y(p.floor).toFixed(1)}` : `M${x(0)},${y(p.floor).toFixed(1)}`)).join(' ');
  const last = pts[pts.length - 1];
  const area = `${line('balance')} L${x(last.day).toFixed(1)},${(H - B).toFixed(1)} L${x(0)},${(H - B).toFixed(1)} Z`;
  const k = (v) => `$${Math.round(v).toLocaleString()}`;
  return (
    <figure className="pfd-chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Balance from ${k(start)} to ${k(last.balance)} over ${last.day} days, against the max-loss floor and the ${k(target)} target`}>
        <defs>
          <linearGradient id="pfdArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.28" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[target, start, pts[0].floor].map((v) => (
          <g key={v} className="pfd-chart-tick">
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} />
            <text x={L - 8} y={y(v) + 4} textAnchor="end">{k(v)}</text>
          </g>
        ))}
        <line className="pfd-chart-target" x1={L} x2={W - R} y1={y(target)} y2={y(target)} />
        <path className="pfd-chart-floor" d={floorPath} />
        <g className="pfd-chart-balance">
          <path d={area} fill="url(#pfdArea)" stroke="none" />
          <path d={line('balance')} fill="none" />
        </g>
        <circle className={`pfd-chart-end is-${outcome}`} cx={x(last.day)} cy={y(last.balance)} r="5" />
        <text className="pfd-chart-x" x={L} y={H - 6}>Start</text>
        <text className="pfd-chart-x" x={W - R} y={H - 6} textAnchor="end">Day {last.day}</text>
      </svg>
      <figcaption className="pfd-chart-legend">
        <span><i className="is-balance" />Balance</span>
        <span><i className="is-target" />Target {k(target)}</span>
        <span><i className="is-floor" />Max-loss floor</span>
      </figcaption>
    </figure>
  );
}

/** One try, day by day: a chart, then the table. */
function RunReplay({ row, rules, windowDays, examples }) {
  const total = row.runs || 0;
  const [run, setRun] = useState(() => examples?.[0]?.run || 1);
  const [input, setInput] = useState(String(examples?.[0]?.run || 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (n) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_ENDPOINTS.propFirmPlannerReplay, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({ rules, windowDays, strategy: row.strategy, size: row.size, run: n }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body?.success) throw new Error(body?.error || 'Couldn’t replay that try.');
      setData(body);
      setInput(String(body.run));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [rules, windowDays, row.strategy, row.size]);

  useEffect(() => { load(run); }, [load, run]);
  const go = (n) => setRun(Math.min(Math.max(1, n), total || 1));

  const label = { passed: 'A typical pass', breached: 'A typical fail', timeout: 'A typical time-out' };
  const outcomeText = data ? ({
    passed: `Passed on day ${data.days}`,
    breached: `Failed on day ${data.days}${data.reason === 'daily loss limit' ? ' (daily loss limit)' : ' (hit the max loss)'}`,
    timeout: `Ran out of time after ${data.days} days`,
  }[data.outcome] || data.outcome) : '';

  return (
    <div className="pfd-replay" aria-label="Replay one try">
      <p className="pfd-lead">
        Pick any of the {plural(total, 'try', 'tries')} to see exactly what happened each day: the result, the balance and how close it came to the max loss.
      </p>
      <div className="pfd-replay-bar">
        {(examples || []).map((e) => (
          <button key={e.outcome} type="button" className={`pfp-chip${data?.run === e.run ? ' is-on' : ''}`} onClick={() => go(e.run)} aria-pressed={data?.run === e.run}>
            {label[e.outcome]}
          </button>
        ))}
        <span className="pfd-replay-pick">
          <button type="button" className="pfp-chip pfp-chip--icon" onClick={() => go(run - 1)} disabled={loading || run <= 1} aria-label="Previous try">‹</button>
          <label>
            Try
            <input
              type="number"
              min={1}
              max={total}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') go(Number(input)); }}
              onBlur={() => { if (Number(input) !== run) go(Number(input)); }}
              aria-label="Try number"
            />
            of {total.toLocaleString()}
          </label>
          <button type="button" className="pfp-chip pfp-chip--icon" onClick={() => go(run + 1)} disabled={loading || run >= total} aria-label="Next try">›</button>
        </span>
      </div>
      {error ? <p className="pfd-warn">{error}</p> : null}
      {data && data.steps ? (
        <>
          <div className={`pfd-replay-sum is-${data.outcome}`}>
            <strong>Try {data.run}: {outcomeText}</strong>
            <span>
              {plural(data.tradedDays, 'day')} with a trade
              {data.profit != null ? ` · ended at ${signed(data.profit)}` : ''}
              {data.cushion != null ? ` · closest to the max loss: ${money(Math.max(0, data.cushion))}` : ''}
            </span>
          </div>
          <BalanceChart steps={data.steps} rules={data.rules || rules} outcome={data.outcome} />
          <details className="pfd-replay-details">
            <summary>Show every day ({data.steps.length})</summary>
            <div className="pfd-replay-wrap">
              <table className="pfd-replay-table">
                <thead>
                  <tr>
                    <th className="num">Day</th>
                    <th className="num">Result at {row.size}</th>
                    <th className="num">Counted</th>
                    <th className="num">Balance</th>
                    <th className="num">Floor</th>
                    <th className="num">Room left</th>
                    <th>What happened</th>
                  </tr>
                </thead>
                <tbody>
                  {data.steps.map((s) => (
                    <tr key={s.day} className={s.traded ? '' : 'is-flat'}>
                      <td className="num">{s.day}</td>
                      <td className={`num ${s.pnl < 0 ? 'neg' : s.pnl > 0 ? 'pos' : ''}`}>{s.traded ? signed(s.pnl) : '—'}</td>
                      <td className={`num ${s.counted !== s.pnl ? 'is-changed' : ''}`}>{s.traded ? signed(s.counted) : '—'}</td>
                      <td className="num">{money(s.balance)}</td>
                      <td className="num">{money(s.floor)}</td>
                      <td className="num">{s.balance != null && s.floor != null ? money(Math.max(0, s.balance - s.floor)) : '—'}</td>
                      <td className="pfd-replay-note">{s.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      ) : loading ? <div className="pfp-skel pfd-chart-skel" aria-label="Replaying…" /> : null}
    </div>
  );
}

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'replay', label: 'Day by day' },
  { id: 'how', label: 'How it’s worked out' },
];

export default function PlannerRowDetail({ row, rules, windowDays, planLabel, onAsk }) {
  const [tab, setTab] = useState('summary');
  const d = row.detail || {};
  const c = d.counts || { passed: 0, breached: 0, timeout: 0 };
  const runs = row.runs || 0;
  const pass = d.daysToPass;
  const day = d.dayStats || {};
  const maxBucket = Math.max(1, ...((pass?.buckets || []).map((b) => b.count)));
  const worstDayOverLimit = rules?.dailyLossLimit && day.worstDay != null && -day.worstDay >= rules.dailyLossLimit;
  const worstDayOverDrawdown = rules?.drawdown && day.worstDay != null && -day.worstDay >= rules.drawdown;
  const ex = explainers({ row, rules, windowDays, d, c, runs, day });

  return (
    <div className="pfd" aria-label={`${row.strategy} at ${row.size}: details`}>
      <div className="pfd-head">
        <div>
          <strong>{row.strategy} · {row.size}</strong>
          <span>
            {planLabel} · started on <X title="Tries" body={ex.runs}>{plural(runs, 'different date')}</X>
            {' · '}<X title="Contract size" body={ex.scale}>{day.scale == null || day.scale === 1 ? 'results per 1 NQ' : `results per 1 NQ × ${day.scale}`}</X>
          </span>
        </div>
        {onAsk ? (
          <button type="button" className="pfp-btn pfp-btn--ghost" onClick={() => onAsk(row)}>
            <IconMessageSquare size={16} /> Ask RiskLo AI about this
          </button>
        ) : null}
      </div>

      <div className="pfd-tabs" role="tablist" aria-label="Details">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'is-on' : ''} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {row.warning ? <p className="pfd-warn">{warningText(row.warning, rules)}</p> : null}
      {runs < SMALL_SAMPLE ? (
        <p className="pfd-warn">
          Only {plural(runs, 'start date')} of history to replay, so these odds can swing a lot as more results come in.
        </p>
      ) : null}

      {tab === 'summary' ? (
        <div className="pfd-pane">
          <div className="pfd-sum-grid">
            <section>
              <h3>How the {plural(runs, 'try', 'tries')} ended</h3>
              <Bar parts={[
                { label: 'Passed', value: c.passed, tone: 'pass' },
                { label: 'Failed', value: c.breached, tone: 'fail' },
                { label: 'Out of time', value: c.timeout, tone: 'time' },
              ]}
              />
              <ul className="pfd-legend">
                <li><i className="pfd-dot pfd-bar-pass" />Passed <X title="Passed" body={ex.passed}><b>{plural(c.passed, 'try', 'tries')}</b> ({pct(c.passed, runs)})</X></li>
                <li><i className="pfd-dot pfd-bar-fail" />Failed <X title="Failed" body={ex.breached}><b>{plural(c.breached, 'try', 'tries')}</b> ({pct(c.breached, runs)})</X></li>
                <li><i className="pfd-dot pfd-bar-time" />Out of time <X title="Out of time" body={ex.timeout}><b>{plural(c.timeout, 'try', 'tries')}</b> ({pct(c.timeout, runs)})</X></li>
              </ul>
              <ul className="pfd-plain">
                <li className="is-pass">
                  {pass ? (
                    <X title="Days to pass" body={ex.daysToPass}>
                      When it passed, it usually took <b>{pass.p25}–{pass.p75} trading days</b> (median {pass.median}). Fastest {pass.fastest}, slowest {pass.slowest}.
                    </X>
                  ) : 'It never passed within the time allowed.'}
                </li>
                <li className="is-fail">
                  {c.breached ? (
                    <X title="Failed" body={ex.breached}>
                      <b>{plural(c.breached, 'try', 'tries')} failed</b>
                      {d.breaches?.byDailyLoss ? `: ${d.breaches.byDrawdown} hit the max loss, ${d.breaches.byDailyLoss} the daily loss limit` : ' by hitting the max loss'}
                      {d.breaches?.medianDay ? `, usually around day ${d.breaches.medianDay}` : ''}.
                    </X>
                  ) : 'It never hit the max loss.'}
                </li>
                <li>
                  <X title="Closest call" body={ex.cushion}>
                    Its closest call was usually <b>{money(d.cushion?.median)}</b> from the max loss; the tightest got within <b>{money(Math.max(0, d.cushion?.worst ?? 0))}</b>.
                  </X>
                </li>
                {c.timeout ? (
                  <li>
                    <X title="Out of time" body={ex.timeout}>
                      <b>{plural(c.timeout, 'try', 'tries')} ran out of time</b>, ending at a median of {signed(d.timeouts?.medianProfit)}
                      {d.timeouts?.reachedTarget ? `; ${d.timeouts.reachedTarget} had hit the target but not the consistency rule or minimum days` : ''}.
                    </X>
                  </li>
                ) : null}
              </ul>
            </section>

            <section>
              <h3>A typical day at {row.size}</h3>
              <dl className="pfd-stats">
                <div><dt>Winning days</dt><dd><X title="Winning days" body={ex.winRate}>{day.winRate == null ? '—' : `${day.winRate}%`}</X></dd></div>
                <div><dt>Average day</dt><dd><X title="Average day" body={ex.avgDay}>{signed(day.avgDay)}</X></dd></div>
                <div><dt>Average win</dt><dd className="pos"><X title="Average win" body={ex.avgWin}>{signed(day.avgWin)}</X></dd></div>
                <div><dt>Average loss</dt><dd className="neg"><X title="Average loss" body={ex.avgLoss}>{signed(day.avgLoss)}</X></dd></div>
                <div><dt>Best day</dt><dd className="pos"><X title="Best day" body={ex.bestDay}>{signed(day.bestDay)}</X></dd></div>
                <div><dt>Worst day</dt><dd className="neg"><X title="Worst day" body={ex.worstDay}>{signed(day.worstDay)}</X></dd></div>
              </dl>
              {worstDayOverDrawdown ? (
                <p className="pfd-warn">Its worst day at this size is bigger than the whole {money(rules.drawdown)} max loss.</p>
              ) : worstDayOverLimit ? (
                <p className="pfd-warn">Its worst day at this size is past the {money(rules.dailyLossLimit)} daily loss limit.</p>
              ) : null}
              <p className="pfd-note">
                From {plural(day.days || 0, 'day')} with a trade{day.flatDays ? ` (and ${plural(day.flatDays, 'weekday')} without one)` : ''}. Tap any number to see how it was worked out.
              </p>
            </section>
          </div>
          <div className="pfd-next">
            <button type="button" className="pfp-btn pfp-btn--ghost" onClick={() => setTab('replay')}>See a try day by day →</button>
          </div>
        </div>
      ) : null}

      {tab === 'replay' && rules ? (
        <div className="pfd-pane">
          <RunReplay row={row} rules={rules} windowDays={windowDays} examples={d.examples} />
        </div>
      ) : null}

      {tab === 'how' ? (
        <div className="pfd-pane pfd-sum-grid">
          <section>
            <h3>How each try is scored</h3>
            <ol className="pfd-steps">
              {evaluationSteps(rules, row.size, windowDays).map((s) => <li key={s}>{s}</li>)}
              <li>That’s repeated starting on each trading day in the strategy’s last three years, and the outcomes are counted.</li>
            </ol>
            {d.alternative ? (
              <p className="pfd-note pfd-assume">
                <b>Days past the daily loss limit:</b>{' '}
                {rules?.dailyLossCount === 'capped'
                  ? <>counted only at the limit here. Counting their full loss instead, it passes <b>{d.alternative.passRate}%</b> and fails <b>{d.alternative.breachRate}%</b>.</>
                  : <>their full loss counts here (cautious). Counting only the limit, as if the firm’s stop closed them there, it passes <b>{d.alternative.passRate}%</b> and fails <b>{d.alternative.breachRate}%</b>.</>}
                {' '}Change this under Customize rules.
              </p>
            ) : null}
          </section>
          <section>
            <h3>How long passing took</h3>
            {pass ? (
              <>
                <p className="pfd-line">
                  <X title="Days to pass" body={ex.daysToPass}>Usually <b>{pass.p25}–{pass.p75} trading days</b> (median {pass.median}). Fastest {pass.fastest}, slowest {pass.slowest}.</X>
                </p>
                <div className="pfd-hist" role="img" aria-label="Passes by trading days taken">
                  {pass.buckets.map((b) => (
                    <div key={b.label} className="pfd-hist-col">
                      <span className="pfd-hist-n">{b.count || ''}</span>
                      <i style={{ height: `${(b.count / maxBucket) * 100}%` }} />
                      <small>{b.label}</small>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="pfd-line">No try passed.</p>}
            {d.waited?.count ? (
              <p className="pfd-note">
                {plural(d.waited.count, 'pass', 'passes')} reached the target early but had to keep trading for the
                {rules?.consistency && rules?.minDays ? ' consistency rule or minimum days' : rules?.consistency ? ' consistency rule' : ' minimum days'}
                {' '}(a median of {plural(d.waited.medianExtraDays, 'extra day')}).
              </p>
            ) : null}
            {rules?.dailyLossLimit && day.overLimitDays ? (
              <p className="pfd-note">
                <X title="Days past the daily loss limit" body={ex.worstDay}>{plural(day.overLimitDays, 'day')}</X> at this size lost {money(rules.dailyLossLimit)} or more.
              </p>
            ) : null}
          </section>
        </div>
      ) : null}
    </div>
  );
}
